/**
 * A ceiling, never the reserve: the box's room is set by CSS from its tallest composed state (`plan-panel.css`), so this only keeps a box that the CSS room did not cover (more card answers than it reserves) from shrinking back; it can never sit lower than the CSS reserve, because the box it measures already has it.
 * Holds a box at the tallest height it has reached while it is on screen: a recompute may change the text inside, but the box never collapses and grows back under the player's eyes.
 * It starts again when `restartOn` changes (a different state of the box, such as the forecast going in). The height is applied as a `min-height` on the element, so content that is taller still shows in full (and raises the held height once).
 */
import { useLayoutEffect, useRef, type RefObject } from "react";

/** The tallest height each named box has reached at each window width, kept when the box unmounts (a step change, a re-keyed page) so the next mount starts at it: one page never shows the box at two heights. */
const KEPT = new Map<string, number>();

export function useHoldHeight<T extends HTMLElement>(restartOn: unknown = null, name?: string): RefObject<T | null> {
  const ref = useRef<T>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el === null || typeof ResizeObserver === "undefined") return;
    const slot = () => (name === undefined ? null : `${name}:${window.innerWidth}`);
    let held = 0;
    const grow = () => {
      const h = Math.ceil(el.getBoundingClientRect().height);
      if (h > held) { held = h; el.style.minHeight = `${h}px`; const k = slot(); if (k !== null) KEPT.set(k, h); }
    };
    const start = slot() === null ? undefined : KEPT.get(slot() as string);
    if (start !== undefined) { held = start; el.style.minHeight = `${start}px`; }
    // A new window size lays the box out afresh: start the hold again from what it needs there.
    const reset = () => { held = 0; el.style.minHeight = ""; const k = slot(); const kept = k === null ? undefined : KEPT.get(k); if (kept !== undefined) { held = kept; el.style.minHeight = `${kept}px`; } grow(); };
    grow();
    const watch = new ResizeObserver(grow);
    watch.observe(el);
    window.addEventListener("resize", reset);
    return () => { watch.disconnect(); window.removeEventListener("resize", reset); };
  }, [restartOn]);
  return ref;
}
